import React, { useEffect, useState } from "react";
import cn from "classnames";
import styles from "./alarm-profile-email-recipients-drawer-modal.module.scss";
import { DrawerModal } from "../drawer-modal/drawer-modal";
import { FlexColumnContainer } from "@src/components/templates/layouts/flex-column-container/flex-column-container";
import { Title } from "@src/components/atoms/title/title";
import { DrawerIconContainer } from "@src/components/atoms/drawer-icon-container/drawer-icon-container";
import envelopeWhite48Icon from "@src/assets/svg/envelope-white-48.svg";
import { ToggleButton } from "@src/components/atoms/toggle-button/toggle-button";
import { FlexRowContainer } from "@src/components/templates/layouts/flex-row-container/flex-row-container";
import userWhiteIcon from "@src/assets/svg/user-white.svg";
import { Button } from "@src/components/atoms/buttons/button/button";
import { HorizontalSeparator } from "@src/components/atoms/horizontal-separator/horizontal-separator";
import bellAlarmWhite from "@src/assets/svg/bell-alarm-white.svg";
import { AlarmProfileEmailRecipientsList } from "@src/components/molecules/alarm-profile-recipients-list/alarm-profile-email-recipients-list";
import { getAlarmProfiles } from "@src/cloud-connect/dependency-injection-container/get-alarm-profiles";
import { isValidEmail } from "./is-valid-email";
import { warningSnackbar } from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import { useI18N } from "@src/language/language-context";
import { RenderIfUserHasPermission } from "@src/components/molecules/render-if-user-has-permission/render-if-user-has-permission";

const cssClassName = "alarm-profile-email-recipients-drawer-modal";

export const AlarmProfileEmailRecipientsDrawerModal = () => {
  const i18n = useI18N();
  const [sendAlarmNotificationsIsActive, setSendAlarmNotificationsIsActive] =
    useState(false);
  const [recipientEmail, setRecipientEmail] = useState("");
  const [localRecipients, setLocalRecipients] = useState([]);

  const texts = {
    title: i18n("modules.alarmProfileEmailRecipientsDrawerModal.title"),
    alarmNotificationsSwitchBoxText: i18n(
      "modules.alarmProfileEmailRecipientsDrawerModal.alarmNotificationsSwitchBoxText",
    ),
    iconAltText: i18n(
      "modules.alarmProfileEmailRecipientsDrawerModal.iconAltText",
    ),
    addAlarmRecipientsTitle: i18n(
      "modules.alarmProfileEmailRecipientsDrawerModal.addAlarmRecipientsTitle",
    ),
    enterEmailAddressText: i18n(
      "modules.alarmProfileEmailRecipientsDrawerModal.enterEmailAddressText",
    ),
    helpText: i18n("modules.alarmProfileEmailRecipientsDrawerModal.helpText"),
    addRecipientButton: i18n(
      "modules.alarmProfileEmailRecipientsDrawerModal.addRecipientButton",
    ),
    recipientsListTitle: i18n(
      "modules.alarmProfileEmailRecipientsDrawerModal.recipientsListTitle",
    ),
    invalidEmailText: i18n(
      "modules.alarmProfileEmailRecipientsDrawerModal.invalidEmailText",
    ),
    duplicateEmailText: i18n(
      "modules.alarmProfileEmailRecipientsDrawerModal.duplicateEmailText",
    ),
  };

  const handleAlarmNotificationsSwitch = () => {
    getAlarmProfiles()
      .getActiveAlarmProfile()
      .getNotifications()
      .getEmail()
      .toggleActive();
  };

  const addRecipient = () => {
    if (isValidEmail(recipientEmail)) {
      const emailNotification = getAlarmProfiles()
        .getActiveAlarmProfile()
        .getNotifications()
        .getEmail();

      const existingRecipients = emailNotification.getRecipients();
      if (existingRecipients.includes(recipientEmail)) {
        warningSnackbar(texts.duplicateEmailText, 2000);
        return;
      }

      emailNotification.addRecipient(recipientEmail);
      setRecipientEmail("");
    } else {
      warningSnackbar(texts.invalidEmailText, 2000);
    }
  };

  useEffect(() => {
    const email = getAlarmProfiles()
      .getActiveAlarmProfile()
      .getNotifications()
      .getEmail();

    const updateRecipients = () => {
      setLocalRecipients(email.getRecipients());
    };

    updateRecipients();

    email.addObserver(updateRecipients);

    return () => {
      email.removeObserver(updateRecipients);
    };
  }, []);

  useEffect(() => {
    const email = getAlarmProfiles()
      .getActiveAlarmProfile()
      .getNotifications()
      .getEmail();

    const updateActiveToggle = () => {
      setSendAlarmNotificationsIsActive(email.getActive());
    };

    updateActiveToggle();

    email.addObserver(updateActiveToggle);

    return () => {
      email.removeObserver(updateActiveToggle);
    };
  }, []);

  return (
    <DrawerModal id={"alarmProfileEmailRecipientsDrawerModal"}>
      <div className={styles[cssClassName]}>
        <div className={styles[`${cssClassName}__upper-container`]}>
          <FlexColumnContainer verticalGap={32}>
            <Title tag="h2" text={texts.title} />
            <DrawerIconContainer icon={envelopeWhite48Icon} />
            <RenderIfUserHasPermission permission="manage-condition-monitoring">
              <div
                className={cn(styles[`${cssClassName}__switch-box`], {
                  [styles[`${cssClassName}__switch-box--active`]]:
                    sendAlarmNotificationsIsActive,
                })}
              >
                <span
                  className={cn(styles[`${cssClassName}__switch-box-text`], {
                    [styles[`${cssClassName}__switch-box-text--active`]]:
                      sendAlarmNotificationsIsActive,
                  })}
                >
                  {texts.alarmNotificationsSwitchBoxText}
                </span>
                <ToggleButton
                  isActive={sendAlarmNotificationsIsActive}
                  handleClick={handleAlarmNotificationsSwitch}
                />
              </div>
              <FlexColumnContainer verticalGap={24}>
                <FlexRowContainer horizontalGap={16}>
                  <img src={userWhiteIcon} alt={texts.iconAltText} />
                  <Title
                    tag="h2"
                    text={texts.addAlarmRecipientsTitle}
                    variant={"thin"}
                  />
                </FlexRowContainer>
                <FlexColumnContainer verticalGap={12}>
                  <FlexRowContainer justifyContent="space-between">
                    <p className={styles[`${cssClassName}__text`]}>
                      {texts.enterEmailAddressText}
                    </p>
                    {/* <FlexRowContainer justifyContent="flex-end">
                    <span
                      className={styles[`${cssClassName}__small-text-gray`]}
                    >
                      {texts.helpText}
                    </span>
                    <img src={questionMarkFullGray12} alt={texts.helpText} />
                  </FlexRowContainer> */}
                  </FlexRowContainer>
                  <input
                    type="text"
                    value={recipientEmail}
                    onChange={(e) => setRecipientEmail(e.target.value)}
                    className={styles[`${cssClassName}__input`]}
                  />
                  <Button fun={addRecipient} text={texts.addRecipientButton} />
                </FlexColumnContainer>
              </FlexColumnContainer>
            </RenderIfUserHasPermission>
          </FlexColumnContainer>
        </div>
      </div>
      <RenderIfUserHasPermission permission="manage-condition-monitoring">
        <HorizontalSeparator />
      </RenderIfUserHasPermission>
      <div className={styles[`${cssClassName}__lower-container`]}>
        <FlexRowContainer horizontalGap={16}>
          <img src={bellAlarmWhite} alt={texts.iconAltText} />
          <Title tag="h2" text={texts.recipientsListTitle} variant={"thin"} />
        </FlexRowContainer>
        <AlarmProfileEmailRecipientsList recipientsList={localRecipients} />
      </div>
    </DrawerModal>
  );
};
